import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { Loader2, Link2, Package2, Pencil, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { packingApiService, BoxType, BoxTypeCreateRequest, BoxTypeUpdateRequest } from '../services/packingApi';

type BoxTypeFormState = {
  name: string;
  description: string;
  length: string;
  width: string;
  height: string;
  weight: string;
  maxQuantity: string;
  cost: string;
  isActive: boolean;
};

const toNumOrUndef = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const toBoxTypeRequest = (s: BoxTypeFormState): BoxTypeCreateRequest => ({
  name: s.name.trim(),
  description: s.description.trim() || undefined,
  length: toNumOrUndef(s.length),
  width: toNumOrUndef(s.width),
  height: toNumOrUndef(s.height),
  weight: toNumOrUndef(s.weight),
  maxQuantity: toNumOrUndef(s.maxQuantity),
  cost: toNumOrUndef(s.cost),
  isActive: s.isActive,
});

const BoxMasterPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<BoxType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<BoxType | null>(null);
  const [form, setForm] = useState<BoxTypeFormState>({
    name: '',
    description: '',
    length: '',
    width: '',
    height: '',
    weight: '',
    maxQuantity: '',
    cost: '',
    isActive: true,
  });

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
  }, [rows]);

  const formatNumber = (value?: number | null) => {
    if (value == null || Number.isNaN(value)) return '—';
    return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await packingApiService.getBoxTypes();
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditingRow(null);
    setForm({
      name: '',
      description: '',
      length: '',
      width: '',
      height: '',
      weight: '',
      maxQuantity: '',
      cost: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (row: BoxType) => {
    setEditingRow(row);
    setForm({
      name: row.name ?? '',
      description: row.description ?? '',
      length: row.length != null ? String(row.length) : '',
      width: row.width != null ? String(row.width) : '',
      height: row.height != null ? String(row.height) : '',
      weight: row.weight != null ? String(row.weight) : '',
      maxQuantity: row.maxQuantity != null ? String(row.maxQuantity) : '',
      cost: row.cost != null ? String(row.cost) : '',
      isActive: Boolean(row.isActive),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Box name is required');
      return;
    }

    try {
      setSaving(true);
      const req = toBoxTypeRequest(form);
      if (editingRow) {
        const updateReq: BoxTypeUpdateRequest = { ...req, id: editingRow.id };
        await packingApiService.updateBoxType(String(editingRow.id), updateReq);
      } else {
        await packingApiService.createBoxType(req);
      }
      setDialogOpen(false);
      await load();
    } catch {
      // handled by service toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <Package2 className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Box Master</CardTitle>
                  <p className="text-sm text-primary-100">Manage box types used in packing</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => void load()}
                  className="border-white/30 bg-white text-primary-700 hover:bg-primary-50"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Refresh
                </Button>
                <Button onClick={openCreate} className="bg-white text-primary-700 hover:bg-primary-50">
                  <Plus className="h-4 w-4 mr-2" />
                  New box
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="text-lg text-primary-800">Box types</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : sortedRows.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No box types found.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">L</TableHead>
                      <TableHead className="text-right">W</TableHead>
                      <TableHead className="text-right">H</TableHead>
                      <TableHead className="text-right">Weight</TableHead>
                      <TableHead className="text-right">Max qty</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-center">Active</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRows.map((row) => (
                      <TableRow key={row.id} className="hover:bg-primary-50/40">
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell className="max-w-[22rem] truncate" title={row.description || ''}>
                          {row.description || '—'}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(row.length)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.width)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.height)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.weight)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.maxQuantity)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.cost)}</TableCell>
                        <TableCell className="text-center">{row.isActive ? 'Yes' : 'No'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-primary-600 hover:bg-primary-100 hover:text-primary-800"
                              aria-label={`Edit ${row.name}`}
                              onClick={() => openEdit(row)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-primary-600 hover:bg-primary-100 hover:text-primary-800"
                              aria-label={`View endgood links for ${row.name}`}
                              title="Endgood links"
                              onClick={() => navigate(`/packing-entries/box-master/endgoods/${row.id}`)}
                            >
                              <Link2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingRow ? 'Edit box type' : 'New box type'}</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="box-name">Name</Label>
                <Input
                  id="box-name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. SMALL BOX"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="box-cost">Cost</Label>
                <Input
                  id="box-cost"
                  type="number"
                  inputMode="decimal"
                  value={form.cost}
                  onChange={(e) => setForm((p) => ({ ...p, cost: e.target.value }))}
                  placeholder="e.g. 12.5"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="box-desc">Description</Label>
                <Input
                  id="box-desc"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Optional"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="box-length">Length</Label>
                <Input
                  id="box-length"
                  type="number"
                  inputMode="decimal"
                  value={form.length}
                  onChange={(e) => setForm((p) => ({ ...p, length: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="box-width">Width</Label>
                <Input
                  id="box-width"
                  type="number"
                  inputMode="decimal"
                  value={form.width}
                  onChange={(e) => setForm((p) => ({ ...p, width: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="box-height">Height</Label>
                <Input
                  id="box-height"
                  type="number"
                  inputMode="decimal"
                  value={form.height}
                  onChange={(e) => setForm((p) => ({ ...p, height: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="box-weight">Weight</Label>
                <Input
                  id="box-weight"
                  type="number"
                  inputMode="decimal"
                  value={form.weight}
                  onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="box-maxqty">Max quantity</Label>
                <Input
                  id="box-maxqty"
                  type="number"
                  inputMode="decimal"
                  value={form.maxQuantity}
                  onChange={(e) => setForm((p) => ({ ...p, maxQuantity: e.target.value }))}
                />
              </div>

              <div className="space-y-2 flex items-end">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.isActive}
                    onCheckedChange={(checked) => setForm((p) => ({ ...p, isActive: checked }))}
                    aria-label="Active"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void handleSave()} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default BoxMasterPage;

